import type { ComponentPropsWithoutRef } from 'react';

import { LoginButton, Logo, Text } from './GoogleLoginButton.styles';
import { GoogleOAuthFlow, type GoogleOAuthParams, useGoogleOAuth } from './hooks';

export enum ButtonSize {
  SMALL = 'small',
  LARGE = 'large',
}

type GoogleOAuthResponse = Pick<GoogleOAuthParams, 'onSuccess' | 'onError'>;

export interface GoogleLoginButtonProps extends ComponentPropsWithoutRef<'button'> {
  size?: Lowercase<ButtonSize>;
  logoSrc?: string;
  onOAuthSuccess: GoogleOAuthResponse['onSuccess'];
  onOAuthError: GoogleOAuthResponse['onError'];
  flow?: GoogleOAuthFlow;
  redirect_uri?: string;
  scope?: string;
  handleClick?: () => void;
  buttonComponent?: React.ComponentType<{ onClick: () => void }>;
}

/**
 * PMTGoogleLoginButton will have default behavior
 * login with google first
 * `onSuccess` => after login with google successfully
 * `onError` => after fail login with google
 */
export const GoogleLoginButton = ({
  size = 'large',
  disabled = false,
  logoSrc = '/images/google-logo.svg',
  onOAuthSuccess,
  onOAuthError,
  children,
  flow,
  redirect_uri,
  scope,
  handleClick,
  buttonComponent: ButtonComponent,
  ...props
}: GoogleLoginButtonProps) => {
  // use configs to generate login method from useGoogleOAuth
  const { login: defaultGoogleOAuth } = useGoogleOAuth({
    onSuccess: onOAuthSuccess,
    onError: onOAuthError,
    flow: flow ?? GoogleOAuthFlow.AuthCode,
    redirect_uri,
    scope,
  });

  if (ButtonComponent) {
    return (
      <ButtonComponent
        onClick={() => {
          defaultGoogleOAuth();
          handleClick?.();
        }}
      />
    );
  }

  return (
    <LoginButton
      size={size}
      disabled={disabled}
      onClick={() => {
        defaultGoogleOAuth();
        handleClick?.();
      }}
      {...props}
    >
      <Logo>
        <img src={logoSrc} alt='google-logo' />
      </Logo>

      <Text>{children}</Text>
    </LoginButton>
  );
};
